import {
  faCalculator,
  faCheckCircle,
  faEnvelope,
  faExclamationTriangle,
  faFileInvoiceDollar,
  faPaperPlane,
  faSync,
  faUsers,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import moment from "moment";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import {
  Badge,
  Button,
  Col,
  Container,
  Input,
  Row,
  Table,
} from "reactstrap";
import {
  ApiBillingDashboard,
  emailMarketingApiService,
} from "../../cores/emailMarketing/services/api";
import { useMeCore } from "../../cores/me";
import { useRouterCore } from "../../cores/router";
import Content from "../layout/Content";
import { Header2 } from "../layout/Header2";
import Layout from "../layout/Layout";
import "../styles/WembiiAdminDashboard.scss";
import "../styles/WembiiAdminSendGridBilling.scss";
import SendGridInvoicesPanel from "./SendGridInvoicesPanel";
import {
  estimateSendGridMarketingCost,
  formatUsd,
  parseCalculatorInput,
  SENDGRID_COST_SCENARIOS,
  SENDGRID_MARKETING_PLANS,
  SENDGRID_PRICING_SOURCE_URL,
} from "./sendgridMarketingPricing";

function toYmd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function formatNum(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return "—";
  return n.toLocaleString();
}

function usageClass(percent: number): string {
  if (percent >= 90) return "danger";
  if (percent >= 75) return "warning";
  return "ok";
}

function getErrorSummary(error: unknown): string {
  const e = error as {
    response?: { status?: number; data?: { message?: string; error?: string } };
    message?: string;
  };
  const status = e.response?.status;
  const serverMessage = e.response?.data?.message || e.response?.data?.error;
  if (status === 404) {
    return "Endpoint no encontrado. Probablemente Lith está corriendo un build viejo sin /email-marketing/admin/billing-dashboard.";
  }
  if (status === 401 || status === 403) {
    return "Permisos insuficientes. Verifica que el usuario sea Wembii admin y que el token esté vigente.";
  }
  if (status) {
    return `Error ${status}${serverMessage ? `: ${serverMessage}` : ""}`;
  }
  return e.message || "Error desconocido al cargar el dashboard.";
}

export default function WembiiAdminSendGridBilling() {
  const intl = useIntl();
  const { user, isAdmin } = useMeCore();
  const { goToSocial } = useRouterCore();

  const [dashboard, setDashboard] = useState<ApiBillingDashboard | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState(() => {
    const now = new Date();
    return toYmd(new Date(now.getFullYear(), now.getMonth(), 1));
  });
  const [dateTo, setDateTo] = useState(() => toYmd(new Date()));
  const [calculatorOpen, setCalculatorOpen] = useState(false);
  const [invoicesOpen, setInvoicesOpen] = useState(false);
  const [calcSubscribers, setCalcSubscribers] = useState("5000");
  const [calcSends, setCalcSends] = useState("15000");

  useEffect(() => {
    if (!user?.userRole?.admin) goToSocial();
  }, [user, goToSocial]);

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const data = await emailMarketingApiService.getAdminBillingDashboard({
        fromDate: dateFrom ? `${dateFrom}T00:00:00` : undefined,
        toDate: dateTo ? `${dateTo}T23:59:59` : undefined,
        limit: 500,
      });
      setDashboard(data);
      setLoadError(null);
    } catch (e) {
      console.error(e);
      setDashboard(null);
      setLoadError(getErrorSummary(e));
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo]);

  useEffect(() => {
    if (!isAdmin) return;
    loadDashboard();
  }, [isAdmin, loadDashboard]);

  const setPresetThisMonth = useCallback(() => {
    const now = new Date();
    setDateFrom(toYmd(new Date(now.getFullYear(), now.getMonth(), 1)));
    setDateTo(toYmd(now));
  }, []);

  const setPresetLastDays = useCallback((days: number) => {
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - days);
    setDateFrom(toYmd(from));
    setDateTo(toYmd(to));
  }, []);

  const filteredCompanies = useMemo(() => {
    if (!dashboard) return [];
    const q = search.trim().toLowerCase();
    if (!q) return dashboard.companies;
    return dashboard.companies.filter(
      (row) =>
        row.companyName.toLowerCase().includes(q) ||
        String(row.companyId).includes(q) ||
        (row.fromEmail || "").toLowerCase().includes(q) ||
        (row.domain || "").toLowerCase().includes(q)
    );
  }, [dashboard, search]);

  const dailyMax = useMemo(() => {
    if (!dashboard?.daily.length) return 1;
    return Math.max(...dashboard.daily.map((d) => d.totalRecipients), 1);
  }, [dashboard]);

  const limits = dashboard?.sendGridAccount.marketingPlanLimits;

  const costEstimate = estimateSendGridMarketingCost(
    parseCalculatorInput(calcSubscribers),
    parseCalculatorInput(calcSends)
  );
  const displayCost = costEstimate.primary;

  const applyScenario = useCallback((subscribers: number, sends: number) => {
    setCalcSubscribers(String(subscribers));
    setCalcSends(String(sends));
    setCalculatorOpen(true);
  }, []);

  if (!isAdmin) return null;

  return (
    <Layout>
      <Header2
        title={intl.formatMessage({ id: "app.sendgridBilling.title" })}
      />
      <Content>
        <Container
          fluid
          className="wembii-admin-dashboard wembii-admin-dashboard--ai py-3 sendgrid-billing"
        >
          <div className="wembii-admin-hero mb-4">
            <div className="wembii-admin-hero__badge">
              <FontAwesomeIcon icon={faEnvelope} />
              <span>
                {intl.formatMessage({ id: "app.sendgridBilling.heroBadge" })}
              </span>
            </div>
            <p className="wembii-admin-hero__subtitle mb-0">
              {intl.formatMessage({ id: "app.sendgridBilling.subtitle" })}
            </p>
          </div>

          <div className="wembii-admin-panel mb-4">
            <Row className="g-2 align-items-end">
              <Col xs="12" md="3">
                <label className="small text-muted mb-1">
                  {intl.formatMessage({ id: "app.sendgridBilling.dateFrom" })}
                </label>
                <Input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                />
              </Col>
              <Col xs="12" md="3">
                <label className="small text-muted mb-1">
                  {intl.formatMessage({ id: "app.sendgridBilling.dateTo" })}
                </label>
                <Input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                />
              </Col>
              <Col xs="12" md="6" className="d-flex flex-wrap gap-2">
                <Button size="sm" color="light" onClick={setPresetThisMonth}>
                  {intl.formatMessage({ id: "app.sendgridBilling.thisMonth" })}
                </Button>
                <Button
                  size="sm"
                  color="light"
                  onClick={() => setPresetLastDays(7)}
                >
                  {intl.formatMessage({ id: "app.sendgridBilling.last7" })}
                </Button>
                <Button
                  size="sm"
                  color="light"
                  onClick={() => setPresetLastDays(30)}
                >
                  {intl.formatMessage({ id: "app.sendgridBilling.last30" })}
                </Button>
                <Button
                  size="sm"
                  color="primary"
                  onClick={loadDashboard}
                  disabled={loading}
                >
                  <FontAwesomeIcon icon={faSync} spin={loading} className="mr-1" />
                  {intl.formatMessage({ id: "app.sendgridBilling.refresh" })}
                </Button>
                <Button
                  size="sm"
                  color="light"
                  onClick={() => setInvoicesOpen(true)}
                >
                  <FontAwesomeIcon icon={faFileInvoiceDollar} className="mr-1" />
                  {intl.formatMessage({ id: "app.sendgridBilling.invoices.open" })}
                </Button>
                <Button
                  size="sm"
                  color={calculatorOpen ? "secondary" : "light"}
                  onClick={() => setCalculatorOpen((open) => !open)}
                >
                  <FontAwesomeIcon icon={faCalculator} className="mr-1" />
                  {intl.formatMessage({ id: "app.sendgridBilling.calculator.toggle" })}
                </Button>
              </Col>
            </Row>
          </div>

          {calculatorOpen && (
            <div className="wembii-admin-panel mb-4 sendgrid-billing__calculator">
              <div className="sendgrid-billing__calculator-head">
                <div>
                  <h6 className="mb-1">
                    {intl.formatMessage({ id: "app.sendgridBilling.calculator.title" })}
                  </h6>
                  <p className="small text-muted mb-0">
                    {intl.formatMessage({ id: "app.sendgridBilling.calculator.subtitle" })}
                  </p>
                </div>
                <a
                  href={SENDGRID_PRICING_SOURCE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="small"
                >
                  {intl.formatMessage({ id: "app.sendgridBilling.calculator.source" })}
                </a>
              </div>

              <Row className="g-3 mt-1">
                <Col xs="12" md="4">
                  <label className="small text-muted mb-1 d-block">
                    {intl.formatMessage({
                      id: "app.sendgridBilling.calculator.subscribers",
                    })}
                  </label>
                  <Input
                    type="number"
                    min={0}
                    step={1}
                    value={calcSubscribers}
                    onChange={(event) => setCalcSubscribers(event.target.value)}
                  />
                </Col>
                <Col xs="12" md="4">
                  <label className="small text-muted mb-1 d-block">
                    {intl.formatMessage({ id: "app.sendgridBilling.calculator.sends" })}
                  </label>
                  <Input
                    type="number"
                    min={0}
                    step={1}
                    value={calcSends}
                    onChange={(event) => setCalcSends(event.target.value)}
                  />
                  <small className="text-muted">
                    {intl.formatMessage({ id: "app.sendgridBilling.calculator.sendsHint" })}
                  </small>
                </Col>
                <Col xs="12" md="4">
                  <div className="sendgrid-billing__calculator-result">
                    <span className="sendgrid-billing__calculator-result-label">
                      {intl.formatMessage({
                        id: "app.sendgridBilling.calculator.estimatedTotal",
                      })}
                    </span>
                    <strong className="sendgrid-billing__calculator-result-value">
                      {formatUsd(displayCost.totalMonthlyUsd)}
                    </strong>
                    <small className="text-muted d-block">
                      {intl.formatMessage(
                        { id: "app.sendgridBilling.calculator.planSummary" },
                        {
                          plan: displayCost.plan.name,
                          contacts: formatNum(displayCost.plan.contactLimit),
                          emails: formatNum(displayCost.plan.emailLimit),
                        }
                      )}
                    </small>
                    {costEstimate.savingsWithRecommended > 0 && (
                      <small className="text-success d-block mt-1">
                        {intl.formatMessage(
                          { id: "app.sendgridBilling.calculator.cheaperPlan" },
                          {
                            plan: costEstimate.recommended.plan.name,
                            amount: formatUsd(costEstimate.recommended.totalMonthlyUsd),
                            savings: formatUsd(costEstimate.savingsWithRecommended),
                          }
                        )}
                      </small>
                    )}
                  </div>
                </Col>
              </Row>

              <div className="sendgrid-billing__calculator-breakdown mt-3">
                <div>
                  <span>{intl.formatMessage({ id: "app.sendgridBilling.calculator.base" })}</span>
                  <strong>{formatUsd(displayCost.baseMonthlyUsd)}</strong>
                </div>
                <div>
                  <span>
                    {intl.formatMessage({ id: "app.sendgridBilling.calculator.overages" })}
                  </span>
                  <strong>{formatUsd(displayCost.overageMonthlyUsd)}</strong>
                </div>
                {displayCost.overageMonthlyUsd > 0 && (
                  <p className="small text-muted mb-0 mt-2">
                    {intl.formatMessage(
                      { id: "app.sendgridBilling.calculator.overagesDetail" },
                      {
                        contacts: formatNum(displayCost.contactOverageUnits),
                        emails: formatNum(displayCost.emailOverageUnits),
                        rate: displayCost.plan.overagePerUnitUsd?.toFixed(4) ?? "0",
                      }
                    )}
                  </p>
                )}
                {!displayCost.fitsWithoutOverage && displayCost.overageMonthlyUsd === 0 && (
                  <p className="small text-muted mb-0 mt-2">
                    {intl.formatMessage({ id: "app.sendgridBilling.calculator.withinPlan" })}
                  </p>
                )}
              </div>

              <div className="sendgrid-billing__calculator-compare mt-3">
                <span className="small text-muted d-block mb-2">
                  {intl.formatMessage({ id: "app.sendgridBilling.calculator.compareTitle" })}
                </span>
                <div className="sendgrid-billing__calculator-compare-grid">
                  {[costEstimate.free, costEstimate.basic, costEstimate.advanced].map(
                    (row) => (
                      <div
                        key={row.plan.id}
                        className={
                          costEstimate.recommended.plan.id === row.plan.id
                            ? "sendgrid-billing__calculator-compare-row is-best"
                            : "sendgrid-billing__calculator-compare-row"
                        }
                      >
                        <span>{row.plan.name}</span>
                        <strong>
                          {!row.eligible
                            ? intl.formatMessage({
                                id: "app.sendgridBilling.calculator.notEligible",
                              })
                            : formatUsd(row.totalMonthlyUsd)}
                        </strong>
                      </div>
                    )
                  )}
                </div>
              </div>

              <div className="sendgrid-billing__calculator-plans mt-3">
                <span className="small text-muted d-block mb-2">
                  {intl.formatMessage({ id: "app.sendgridBilling.calculator.officialPlans" })}
                </span>
                <div className="sendgrid-billing__calculator-plan-grid">
                  {SENDGRID_MARKETING_PLANS.map((plan) => (
                    <div
                      key={plan.id}
                      className={
                        displayCost.plan.id === plan.id
                          ? "sendgrid-billing__calculator-plan is-active"
                          : "sendgrid-billing__calculator-plan"
                      }
                    >
                      <strong>{plan.name}</strong>
                      <span>{formatUsd(plan.monthlyUsd)}/mo</span>
                      <small>
                        {intl.formatMessage(
                          { id: "app.sendgridBilling.calculator.planLimits" },
                          {
                            contacts: formatNum(plan.contactLimit),
                            emails: formatNum(plan.emailLimit),
                          }
                        )}
                      </small>
                    </div>
                  ))}
                </div>
              </div>

              <div className="sendgrid-billing__calculator-scenarios mt-4">
                <h6 className="mb-2">
                  {intl.formatMessage({ id: "app.sendgridBilling.calculator.scenariosTitle" })}
                </h6>
                <div className="sendgrid-billing__calculator-scenario-grid">
                  {SENDGRID_COST_SCENARIOS.map((scenario) => {
                    const scenarioEstimate = estimateSendGridMarketingCost(
                      scenario.subscribers,
                      scenario.monthlySends
                    );
                    return (
                      <button
                        key={scenario.id}
                        type="button"
                        className="sendgrid-billing__calculator-scenario"
                        onClick={() =>
                          applyScenario(scenario.subscribers, scenario.monthlySends)
                        }
                      >
                        <strong>{intl.formatMessage({ id: scenario.labelKey })}</strong>
                        <span>
                          {formatNum(scenario.subscribers)}{" "}
                          {intl.formatMessage({
                            id: "app.sendgridBilling.calculator.subscribersShort",
                          })}{" "}
                          · {formatNum(scenario.monthlySends)}{" "}
                          {intl.formatMessage({ id: "app.sendgridBilling.emails" })}
                        </span>
                        <em>
                          {scenarioEstimate.recommended.eligible
                            ? formatUsd(scenarioEstimate.recommended.totalMonthlyUsd)
                            : formatUsd(scenarioEstimate.basic.totalMonthlyUsd)}
                          /mo
                        </em>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {dashboard && (
            <>
              <Row className="g-3 mb-4">
                <Col xs="12" md="6" xl="3">
                  <div className="wembii-admin-kpi wembii-admin-kpi--companies">
                    <div className="wembii-admin-kpi__icon">
                      <FontAwesomeIcon icon={faPaperPlane} />
                    </div>
                    <div className="wembii-admin-kpi__text">
                      <div className="wembii-admin-kpi__label">
                        {intl.formatMessage({
                          id: "app.sendgridBilling.kpi.emailsSent",
                        })}
                      </div>
                      <div className="wembii-admin-kpi__value">
                        {formatNum(dashboard.totals.totalRecipients)}
                      </div>
                      <small className="wembii-admin-kpi__hint">
                        {intl.formatMessage(
                          { id: "app.sendgridBilling.kpi.operations" },
                          { count: dashboard.totals.totalSendOperations }
                        )}
                      </small>
                    </div>
                  </div>
                </Col>
                <Col xs="12" md="6" xl="3">
                  <div className="wembii-admin-kpi wembii-admin-kpi--users">
                    <div className="wembii-admin-kpi__icon">
                      <FontAwesomeIcon icon={faUsers} />
                    </div>
                    <div className="wembii-admin-kpi__text">
                      <div className="wembii-admin-kpi__label">
                        {intl.formatMessage({
                          id: "app.sendgridBilling.kpi.listContacts",
                        })}
                      </div>
                      <div className="wembii-admin-kpi__value">
                        {formatNum(dashboard.totals.totalListContacts)}
                      </div>
                      <small className="wembii-admin-kpi__hint">
                        {intl.formatMessage(
                          { id: "app.sendgridBilling.kpi.crmContacts" },
                          {
                            count: dashboard.totals.totalCrmContactsWithEmail,
                          }
                        )}
                      </small>
                    </div>
                  </div>
                </Col>
                <Col xs="12" md="6" xl="3">
                  <div className="wembii-admin-kpi wembii-admin-kpi--credits">
                    <div className="wembii-admin-kpi__icon">
                      <FontAwesomeIcon icon={faEnvelope} />
                    </div>
                    <div className="wembii-admin-kpi__text">
                      <div className="wembii-admin-kpi__label">
                        {intl.formatMessage({
                          id: "app.sendgridBilling.kpi.marketingPlan",
                        })}
                      </div>
                      <div className="wembii-admin-kpi__value">
                        {dashboard.sendGridAccount.marketingPlan}
                      </div>
                      <small className="wembii-admin-kpi__hint">
                        {limits
                          ? intl.formatMessage(
                              { id: "app.sendgridBilling.kpi.marketingLimits" },
                              {
                                contacts: limits.contacts,
                                emails: limits.marketingEmailsPerMonth,
                              }
                            )
                          : null}
                      </small>
                    </div>
                  </div>
                </Col>
                <Col xs="12" md="6" xl="3">
                  <div className="wembii-admin-kpi wembii-admin-kpi--companies">
                    <div className="wembii-admin-kpi__icon">
                      <FontAwesomeIcon icon={faSync} />
                    </div>
                    <div className="wembii-admin-kpi__text">
                      <div className="wembii-admin-kpi__label">
                        {intl.formatMessage({
                          id: "app.sendgridBilling.kpi.apiCredits",
                        })}
                      </div>
                      <div className="wembii-admin-kpi__value">
                        {formatNum(
                          dashboard.sendGridAccount.credits?.remain
                        )}
                      </div>
                      <small className="wembii-admin-kpi__hint">
                        {dashboard.sendGridAccount.credits?.total != null
                          ? intl.formatMessage(
                              {
                                id: "app.sendgridBilling.kpi.apiCreditsHint",
                              },
                              {
                                used: formatNum(
                                  dashboard.sendGridAccount.credits?.used
                                ),
                                total: formatNum(
                                  dashboard.sendGridAccount.credits?.total
                                ),
                              }
                            )
                          : intl.formatMessage({
                              id: "app.sendgridBilling.kpi.apiCreditsUnknown",
                            })}
                      </small>
                    </div>
                  </div>
                </Col>
              </Row>

              <Row className="g-3 mb-4">
                <Col xs="12" lg="4">
                  <div className="sendgrid-billing__risk-card">
                    <FontAwesomeIcon
                      icon={
                        dashboard.totals.projectedFullAudienceWouldExceed
                          ? faExclamationTriangle
                          : faCheckCircle
                      }
                      className={
                        dashboard.totals.projectedFullAudienceWouldExceed
                          ? "text-warning"
                          : "text-success"
                      }
                    />
                    <div>
                      <strong>
                        {intl.formatMessage({
                          id: "app.sendgridBilling.risk.fullAudience",
                        })}
                      </strong>
                      <p className="mb-0 text-muted">
                        {intl.formatMessage(
                          {
                            id: dashboard.totals
                              .projectedFullAudienceWouldExceed
                              ? "app.sendgridBilling.risk.fullAudienceDanger"
                              : "app.sendgridBilling.risk.fullAudienceOk",
                          },
                          {
                            projected:
                              dashboard.totals.projectedFullAudienceRecipients,
                            remaining:
                              dashboard.totals.remainingMarketingEmails,
                          }
                        )}
                      </p>
                    </div>
                  </div>
                </Col>
                <Col xs="12" lg="4">
                  <div className="sendgrid-billing__risk-card">
                    <FontAwesomeIcon icon={faUsers} />
                    <div>
                      <strong>
                        {intl.formatMessage({
                          id: "app.sendgridBilling.risk.remaining",
                        })}
                      </strong>
                      <p className="mb-0 text-muted">
                        {intl.formatMessage(
                          { id: "app.sendgridBilling.risk.remainingHint" },
                          {
                            emails:
                              dashboard.totals.remainingMarketingEmails,
                            contacts:
                              dashboard.totals.remainingMarketingContacts,
                          }
                        )}
                      </p>
                    </div>
                  </div>
                </Col>
                <Col xs="12" lg="4">
                  <div className="sendgrid-billing__risk-card">
                    <FontAwesomeIcon icon={faExclamationTriangle} />
                    <div>
                      <strong>
                        {intl.formatMessage({
                          id: "app.sendgridBilling.risk.config",
                        })}
                      </strong>
                      <p className="mb-0 text-muted">
                        {intl.formatMessage(
                          { id: "app.sendgridBilling.risk.configHint" },
                          {
                            local: dashboard.totals.companiesLocalList,
                            sender: dashboard.totals.companiesMissingSender,
                            domain:
                              dashboard.totals.companiesMissingDomainAuth,
                          }
                        )}
                      </p>
                    </div>
                  </div>
                </Col>
              </Row>

              <Row className="g-3 mb-4">
                <Col xs="12" lg="4">
                  <div className="wembii-admin-panel h-100">
                    <h6 className="mb-3">
                      {intl.formatMessage({
                        id: "app.sendgridBilling.usage.marketingEmails",
                      })}
                    </h6>
                    <div
                      className={`sendgrid-billing__progress sendgrid-billing__progress--${usageClass(
                        dashboard.totals.marketingEmailsUsedPercent
                      )}`}
                    >
                      <div
                        className="sendgrid-billing__progress-bar"
                        style={{
                          width: `${dashboard.totals.marketingEmailsUsedPercent}%`,
                        }}
                      />
                    </div>
                    <p className="small text-muted mt-2 mb-0">
                      {intl.formatMessage(
                        { id: "app.sendgridBilling.usage.marketingEmailsHint" },
                        {
                          used: dashboard.totals.totalRecipients,
                          limit: limits?.marketingEmailsPerMonth ?? 0,
                          percent: dashboard.totals.marketingEmailsUsedPercent,
                        }
                      )}
                    </p>
                  </div>
                </Col>
                <Col xs="12" lg="4">
                  <div className="wembii-admin-panel h-100">
                    <h6 className="mb-3">
                      {intl.formatMessage({
                        id: "app.sendgridBilling.usage.contacts",
                      })}
                    </h6>
                    <div
                      className={`sendgrid-billing__progress sendgrid-billing__progress--${usageClass(
                        dashboard.totals.marketingContactsUsedPercent
                      )}`}
                    >
                      <div
                        className="sendgrid-billing__progress-bar"
                        style={{
                          width: `${dashboard.totals.marketingContactsUsedPercent}%`,
                        }}
                      />
                    </div>
                    <p className="small text-muted mt-2 mb-0">
                      {intl.formatMessage(
                        { id: "app.sendgridBilling.usage.contactsHint" },
                        {
                          used: dashboard.totals.totalListContacts,
                          limit: limits?.contacts ?? 0,
                          percent:
                            dashboard.totals.marketingContactsUsedPercent,
                        }
                      )}
                    </p>
                  </div>
                </Col>
                <Col xs="12" lg="4">
                  <div className="wembii-admin-panel h-100">
                    <h6 className="mb-3">
                      {intl.formatMessage({
                        id: "app.sendgridBilling.usage.byType",
                      })}
                    </h6>
                    {dashboard.bySendType.length === 0 ? (
                      <p className="small text-muted mb-0">
                        {intl.formatMessage({
                          id: "app.sendgridBilling.noData",
                        })}
                      </p>
                    ) : (
                      <ul className="sendgrid-billing__type-list mb-0">
                        {dashboard.bySendType.map((row) => (
                          <li key={row.sendType}>
                            <Badge color="secondary" className="mr-2">
                              {row.sendType}
                            </Badge>
                            {formatNum(row.totalRecipients)}{" "}
                            {intl.formatMessage({
                              id: "app.sendgridBilling.emails",
                            })}
                            <span className="text-muted">
                              ({row.sendOperations}{" "}
                              {intl.formatMessage({
                                id: "app.sendgridBilling.ops",
                              })})
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </Col>
              </Row>

              {dashboard.daily.length > 0 && (
                <div className="wembii-admin-panel mb-4">
                  <h6 className="mb-3">
                    {intl.formatMessage({
                      id: "app.sendgridBilling.dailyChart",
                    })}
                  </h6>
                  <div className="sendgrid-billing__daily">
                    {dashboard.daily.map((row) => (
                      <div key={row.day} className="sendgrid-billing__daily-col">
                        <div
                          className="sendgrid-billing__daily-bar"
                          style={{
                            height: `${(row.totalRecipients / dailyMax) * 100}%`,
                          }}
                          title={`${row.totalRecipients} emails`}
                        />
                        <span className="sendgrid-billing__daily-label">
                          {moment(row.day).format("DD/MM")}
                        </span>
                        <span className="sendgrid-billing__daily-value">
                          {row.totalRecipients}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="wembii-admin-panel mb-4">
                <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                  <h6 className="mb-0">
                    {intl.formatMessage({
                      id: "app.sendgridBilling.companiesTable",
                    })}
                  </h6>
                  <Input
                    type="search"
                    placeholder={intl.formatMessage({
                      id: "app.sendgridBilling.searchPlaceholder",
                    })}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="sendgrid-billing__search"
                  />
                </div>
                <div className="table-responsive">
                  <Table className="wembii-admin-table" hover size="sm">
                    <thead>
                      <tr>
                        <th>{intl.formatMessage({ id: "app.sendgridBilling.col.company" })}</th>
                        <th>{intl.formatMessage({ id: "app.sendgridBilling.col.emailsSent" })}</th>
                        <th>{intl.formatMessage({ id: "app.sendgridBilling.col.ops" })}</th>
                        <th>{intl.formatMessage({ id: "app.sendgridBilling.col.listContacts" })}</th>
                        <th>{intl.formatMessage({ id: "app.sendgridBilling.col.crmContacts" })}</th>
                        <th>{intl.formatMessage({ id: "app.sendgridBilling.col.sender" })}</th>
                        <th>{intl.formatMessage({ id: "app.sendgridBilling.col.domain" })}</th>
                        <th>{intl.formatMessage({ id: "app.sendgridBilling.col.lastSend" })}</th>
                        <th>{intl.formatMessage({ id: "app.sendgridBilling.col.risk" })}</th>
                        <th>{intl.formatMessage({ id: "app.sendgridBilling.col.status" })}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredCompanies.map((row) => (
                        <tr key={row.companyId}>
                          <td>
                            <strong>{row.companyName}</strong>
                            <br />
                            <small className="text-muted">#{row.companyId}</small>
                          </td>
                          <td>{formatNum(row.totalRecipients)}</td>
                          <td>{row.sendOperations}</td>
                          <td>{formatNum(row.listContactCount)}</td>
                          <td>{formatNum(row.crmContactsWithEmail)}</td>
                          <td>
                            {row.fromEmail || "—"}
                            {row.isLocalList && (
                              <Badge color="warning" className="ml-1">
                                local
                              </Badge>
                            )}
                          </td>
                          <td>
                            {row.domain || "—"}
                            {row.domainAuthenticated && (
                              <Badge color="success" className="ml-1">
                                OK
                              </Badge>
                            )}
                          </td>
                          <td>
                            {row.lastSendAt
                              ? moment(row.lastSendAt).format("DD/MM/YYYY")
                              : "—"}
                          </td>
                          <td>
                            <small>
                              {row.billingRisk.periodEmailPlanPercent}% email ·{" "}
                              {row.billingRisk.contactPlanPercent}% contacts
                              {row.billingRisk.crmNotSyncedCount > 0 && (
                                <>
                                  <br />
                                  <span className="text-warning">
                                    +{row.billingRisk.crmNotSyncedCount} CRM no sync
                                  </span>
                                </>
                              )}
                            </small>
                          </td>
                          <td>
                            <Badge
                              color={row.marketingReady ? "success" : "secondary"}
                            >
                              {row.marketingReady
                                ? intl.formatMessage({
                                    id: "app.sendgridBilling.status.ready",
                                  })
                                : intl.formatMessage({
                                    id: "app.sendgridBilling.status.pending",
                                  })}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                      {filteredCompanies.length === 0 && (
                        <tr>
                          <td colSpan={10} className="text-center text-muted">
                            {intl.formatMessage({
                              id: "app.sendgridBilling.noData",
                            })}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              </div>

              <div className="wembii-admin-panel">
                <h6 className="mb-3">
                  {intl.formatMessage({
                    id: "app.sendgridBilling.recentActivity",
                  })}
                </h6>
                <div className="table-responsive">
                  <Table className="wembii-admin-table" hover size="sm">
                    <thead>
                      <tr>
                        <th>{intl.formatMessage({ id: "app.sendgridBilling.col.date" })}</th>
                        <th>{intl.formatMessage({ id: "app.sendgridBilling.col.company" })}</th>
                        <th>{intl.formatMessage({ id: "app.sendgridBilling.col.type" })}</th>
                        <th>{intl.formatMessage({ id: "app.sendgridBilling.col.recipients" })}</th>
                        <th>{intl.formatMessage({ id: "app.sendgridBilling.col.subject" })}</th>
                        <th>{intl.formatMessage({ id: "app.sendgridBilling.col.from" })}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dashboard.recentLogs.map((log) => (
                        <tr key={log.id}>
                          <td>
                            {moment(log.createdAt).format("DD/MM/YYYY HH:mm")}
                          </td>
                          <td>
                            {log.companyName || `#${log.companyId}`}
                          </td>
                          <td>
                            <Badge color="info">{log.sendType}</Badge>
                          </td>
                          <td>{formatNum(log.recipientCount)}</td>
                          <td className="sendgrid-billing__subject">
                            {log.subject || "—"}
                          </td>
                          <td>{log.fromEmail || "—"}</td>
                        </tr>
                      ))}
                      {dashboard.recentLogs.length === 0 && (
                        <tr>
                          <td colSpan={6} className="text-center text-muted">
                            {intl.formatMessage({
                              id: "app.sendgridBilling.noData",
                            })}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              </div>
            </>
          )}

          {!dashboard && !loading && (
            <div className="sendgrid-billing__error">
              <FontAwesomeIcon icon={faExclamationTriangle} />
              <div>
                <strong>
                  {intl.formatMessage({ id: "app.sendgridBilling.loadError" })}
                </strong>
                {loadError && <p className="mb-0">{loadError}</p>}
              </div>
            </div>
          )}
        </Container>
        <SendGridInvoicesPanel
          open={invoicesOpen}
          onClose={() => setInvoicesOpen(false)}
        />
      </Content>
    </Layout>
  );
}
